/**
 * A place's members (place manager, unit 3): how many, and who. The reads
 * come in through {@link MembersApi}, so the counting is unit-testable without
 * a network; the real reads live in `place-members-api.ts`.
 *
 * A member, today, is a person who follows one of the place's seasons: who
 * joined one, or played a scored event in one (every scorer is made a
 * follower). The backend has no place roster, no leaving and no visits to
 * read, so the page says that plainly and never mixes members with the live
 * headcount ("N here now", unit 1).
 *
 * Two steps, because `Season.followers` is unpaged (every follower, every
 * time): {@link readMemberCounts} reads each season's count alone, for the
 * People card and the section's summary; {@link readMemberLists} reads the
 * lists (the faces) only when the partner opens the members, once per visit.
 *
 * Which seasons: the same ones "How the last ones went" reads (unit 2's
 * {@link seasonsToRead}: of those that have begun, the most recently
 * running), from the one seasons read both sections share. Seasons still to
 * come and older ones are not read; when the place has any, the count is a
 * floor ("N+") and the section says where it is from.
 */
import type { PlaceSeasonsData } from "@/data/place-events-api";
import type { SeasonFollowersData } from "@/data/place-members-api";
import { faceOf, type Face } from "@/features/places/face";
import { seasonsToRead } from "@/features/places/last-events";

/** How many faces a season's list shows before "+N more". */
export const MEMBERS_SHOWN = 12;

type Season = PlaceSeasonsData["seasons"]["nodes"][number];

/** The reads this section makes (`place-members-api.ts`, with the viewer
 *  bound). Null: the season is not readable by this viewer. */
export interface MembersApi {
  /** How many follow the season: the count alone. */
  followerCount(seasonId: string): Promise<number | null>;
  /** Everyone following the season (unpaged). */
  followers(seasonId: string): Promise<SeasonFollowersData | null>;
}

/** One season as read: its count (read, not readable by this viewer, or the
 *  read failed) and, once the members are opened, its list. */
export interface SeasonFollowersRead {
  season: Season;
  count: number | null | "failed";
  /** Undefined until the list is read. */
  list?: SeasonFollowersData | null | "failed";
}

/** One season's members, as the section draws them. */
export interface SeasonMembers {
  id: string;
  title: string;
  /** "shown": counted; "hidden": not shown to this viewer; "failed": did not read. */
  state: "shown" | "hidden" | "failed";
  /** People following the season; null unless shown. */
  total: number | null;
  /** The first {@link MEMBERS_SHOWN} of them; null while the list is not read
   *  (or did not read: `listFailed`). */
  faces: Face[] | null;
  /** The list was asked for and did not read. */
  listFailed: boolean;
  /** Those beyond the faces shown (the "+N more"); 0 until the list is read. */
  more: number;
}

/** What the section and the People card draw for one place. */
export interface PlaceMembers {
  /** Distinct members across the seasons read once their lists are read
   *  (someone following two counts once). Before that, the largest season's
   *  count: the seasons may share people, so adding them would overcount. */
  count: number;
  /** `count` is a floor: the lists are not read and more than one season has
   *  members, a list came back short of its count, a season's members did not
   *  read or are not shown to this viewer, or the place has seasons that were
   *  not read. */
  atLeast: boolean;
  /** The lists: not asked for yet, being read, or read (each season's list
   *  read or said as not read). */
  lists: "unread" | "reading" | "read";
  /** One per season read, in {@link seasonsToRead}'s order. */
  groups: SeasonMembers[];
  /** Seasons read whose members are not shown to this viewer. */
  notShown: number;
  /** Seasons read, of all the place's seasons this viewer sees; `cut` when
   *  some were not read (still to come, older, or past the first listing). */
  seasons: { read: number; total: number; cut: boolean };
}

/** A to Z by name, then by id, so the same people show on every visit (the
 *  backend lists followers in no fixed order, and has no join date to sort by). */
function byName(a: Face, b: Face): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }) || a.id.localeCompare(b.id);
}

function groupOf({ season, count, list }: SeasonFollowersRead): SeasonMembers {
  const base = { id: season.id, title: season.title, faces: null, listFailed: false, more: 0 };
  if (count === "failed") return { ...base, state: "failed", total: null };
  if (count === null) return { ...base, state: "hidden", total: null };
  if (list === undefined) return { ...base, state: "shown", total: count };
  if (list === null || list === "failed") return { ...base, state: "shown", total: count, listFailed: true };
  const people = new Map<string, Face>();
  for (const u of list.nodes) if (!people.has(u.id)) people.set(u.id, faceOf(u));
  const faces = [...people.values()].sort(byName).slice(0, MEMBERS_SHOWN);
  // The list's own count is the fresher; "+N more" follows it, as unit 1's
  // "+N" follows the headcount.
  return { ...base, state: "shown", total: list.totalCount, faces, more: Math.max(0, list.totalCount - faces.length) };
}

/** The section's model from what was read. `reading`: the lists are being
 *  read. With every shown season's list read, the count is the people across
 *  them, each once, never below any one season's count (a list can come back
 *  short of it: a follower whose account did not load). Without the lists, it
 *  is the largest season's count, a floor whenever two seasons have members. */
export function placeMembersOf(seasons: PlaceSeasonsData, reads: readonly SeasonFollowersRead[], reading = false): PlaceMembers {
  const groups = reads.map(groupOf);
  const shown = groups.filter((g) => g.state === "shown");
  const largest = Math.max(0, ...shown.map((g) => g.total ?? 0));
  const listed = reads.filter((r) => typeof r.count === "number").every((r) => r.list && r.list !== "failed");
  let count = largest;
  let floor = false;
  if (listed) {
    const people = new Set<string>();
    for (const { list } of reads) {
      if (!list || list === "failed") continue;
      for (const u of list.nodes) people.add(u.id);
      if (list.nodes.length < list.totalCount) floor = true;
    }
    count = Math.max(people.size, largest);
  } else {
    floor = shown.filter((g) => (g.total ?? 0) > 0).length > 1 || shown.some((g) => g.listFailed);
  }
  const total = seasons.seasons.totalCount;
  const cut = reads.length < total;
  const asked = reads.some((r) => r.list !== undefined);
  return {
    count,
    atLeast: floor || cut || groups.some((g) => g.state !== "shown"),
    lists: reading ? "reading" : asked || shown.length === 0 ? "read" : "unread",
    groups,
    notShown: groups.filter((g) => g.state === "hidden").length,
    seasons: { read: reads.length, total, cut },
  };
}

/** Each season's follower count, side by side, for the seasons read (the
 *  place's seasons come in, read once and shared with unit 2). A season whose
 *  count does not read is said in its own group and leaves the others
 *  standing. */
export async function readMemberCounts(seasons: PlaceSeasonsData, api: MembersApi, now: number): Promise<SeasonFollowersRead[]> {
  return Promise.all(
    seasonsToRead(seasons.seasons.nodes, now).map(async (season): Promise<SeasonFollowersRead> => {
      try {
        return { season, count: await api.followerCount(season.id) };
      } catch {
        return { season, count: "failed" };
      }
    }),
  );
}

/** The lists (the faces) of the seasons counted, side by side; only those
 *  whose count read (the others have nothing to show). A list that does not
 *  read is said in its group; its count stands. */
export async function readMemberLists(reads: readonly SeasonFollowersRead[], api: MembersApi): Promise<SeasonFollowersRead[]> {
  return Promise.all(
    reads.map(async (read): Promise<SeasonFollowersRead> => {
      if (typeof read.count !== "number") return read;
      try {
        return { ...read, list: await api.followers(read.season.id) };
      } catch {
        return { ...read, list: "failed" };
      }
    }),
  );
}
